import React, { useState } from 'react';
import { Tag, Popconfirm } from 'antd';
import { isProjectLive } from '../../utils';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import { saveDocumentContent } from '../../api';
import SimpleEditor from '../ESR/editor/texteditor/texteditor';

type ContentEditorProps = {
  saveData: (data: any) => void;
  project: any;
  entityId: string;
  queryKey: string;
  id: string;
  data: any;
  sectionType?: string;
  tableDataId?: string;
};

const ContentEditor: React.FC<ContentEditorProps> = ({
  data,
  project,
  id,
  entityId,
  queryKey,
  saveData,
  sectionType,
  tableDataId
}) => {
  const client = useQueryClient();
  const isNotEditable = isProjectLive(project);

  const { mutate: syncContent, isLoading } = useMutation(saveDocumentContent, {
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: (e) => {
      console.error('Unable to save content:', e);
    }
  });

  let isShared = false;
  let dataToRender = data?.content || '';
  const tagsList: string[] = [];

  if (!dataToRender && data?.shared_content_paragraph) {
    isShared = true;
    dataToRender = data.shared_content_paragraph.content;
    const shared = data.shared_content_paragraph;
    if (shared.country) tagsList.push(shared.country);
    if (shared.project_type_group) tagsList.push(shared.project_type_group);
    if (shared.project_type) tagsList.push(shared.project_type);
    if (shared.ghg_methodology) tagsList.push(shared.ghg_methodology);
    if (shared.ghg_standard) tagsList.push(shared.ghg_standard);
  }

  const [canEdit, setCanEdit] = useState(!isShared);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleContentChange = (content: string) => {
    const documentID = `${process.env.NODE_ENV}_ckeditor_${entityId}_${id}`;
    syncContent({
      documentID,
      ratingBlurbSectionParagraphId: sectionType === 'paragraph' ? id : undefined,
      ratingTableDataId: sectionType === 'table' ? tableDataId : undefined,
      content
    });
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {isShared && (
        <div className="flex gap-2">
          <Tag color="green">Shared</Tag>
          {tagsList.map((tag) => (
            <Tag key={tag} color="blue">
              {tag}
            </Tag>
          ))}
        </div>
      )}
      <Popconfirm
        open={showConfirm}
        placement='topRight'
        title="Are you sure you want to edit shared text?"
        description="Editing this section will override this text for this project only. Any further changes to shared content will not be reflected here."
        icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
        onConfirm={() => {
          setCanEdit(true);
          setShowConfirm(false);
        }}
        onCancel={() => setShowConfirm(false)}
        okText="Yes, edit for this project"
        cancelText="No">
        <div style={{ position: 'relative' }}>
          <SimpleEditor
            initialData={dataToRender}
            editable={!isNotEditable && canEdit}
            onChange={handleContentChange}
          />

          {isShared && !isNotEditable && !canEdit && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                cursor: 'pointer'
              }}
              onClick={() => setShowConfirm(true)}
            />
          )}
        </div>
      </Popconfirm>
    </div>
  );
};

export default ContentEditor;
